import type { AbstractError } from '@/domain/errors/abstract-error.js';
import { BadRequestError } from '@/domain/errors/bad-request-error.js';
import { ForbiddenError } from '@/domain/errors/forbidden-error.js';
import { NotFoundError } from '@/domain/errors/not-found-error.js';
import { ServerError } from '@/domain/errors/server-error.js';
import { UnauthorizedError } from '@/domain/errors/unauthorized-error.js';
import type { HttpClient } from '@/domain/protocols/http-client.js';
import { useAuthStore } from '@/infra/stores/auth-store.js';

// Module-level (not per-instance) so every FetchHttpClient created across the app shares one
// in-flight refresh — concurrent 401s (e.g. polling requests fired via Promise.all) await the
// same refresh instead of each rotating the refresh token independently.
let refreshPromise: Promise<boolean> | null = null;

export class FetchHttpClient implements HttpClient {
    public async request<T>(params: HttpClient.Request): Promise<HttpClient.Response<T>> {
        let response = await this.doRequest(params);
        if (response.status === 401 && !this.isAgentEfUrl(params.url)) {
            const refreshed = await this.tryRefresh();
            if (refreshed) {
                response = await this.doRequest(params);
            }
        }
        if (response.ok && params.responseType === 'blob') {
            return { statusCode: response.status, body: (await response.blob()) as T, headers: Object.fromEntries(response.headers.entries()) };
        }
        const body = await response.json().catch(() => null);
        if (!response.ok) {
            throw this.mapError(response.status, body);
        }
        return { statusCode: response.status, body: body as T };
    }

    private async doRequest(params: HttpClient.Request): Promise<Response> {
        const token = this.getToken();
        const isFormData = params.body instanceof FormData;
        const headers: Record<string, string> = {
            ...(params.body !== undefined && !isFormData ? { 'Content-Type': 'application/json' } : {}),
            ...params.headers
        };
        if (token) {
            headers['Authorization'] = `Bearer ${token}`;
        }
        try {
            let body: BodyInit | undefined;
            if (params.body === undefined) {
                body = undefined;
            } else if (isFormData) {
                body = params.body as FormData;
            } else {
                body = JSON.stringify(params.body);
            }
            return await fetch(params.url, {
                method: params.method,
                headers,
                body,
                ...(params.credentials ? { credentials: params.credentials } : {})
            });
        } catch {
            throw new ServerError('Erro ao conectar com o servidor');
        }
    }

    private isAgentEfUrl(url: string): boolean {
        return url.startsWith('/agent-ef');
    }

    private getToken(): string | null {
        try {
            return localStorage.getItem('accessToken');
        } catch {
            return null;
        }
    }

    private async tryRefresh(): Promise<boolean> {
        if (!refreshPromise) {
            refreshPromise = this.doRefresh().finally(() => {
                refreshPromise = null;
            });
        }
        return refreshPromise;
    }

    private async doRefresh(): Promise<boolean> {
        const refreshToken = localStorage.getItem('refreshToken');
        if (!refreshToken) {
            this.expireSession();
            return false;
        }
        try {
            const response = await fetch('/api/auth/refresh', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ refreshToken })
            });
            if (!response.ok) {
                this.expireSession();
                return false;
            }
            const data = (await response.json()) as { accessToken: string; refreshToken: string };
            localStorage.setItem('accessToken', data.accessToken);
            localStorage.setItem('refreshToken', data.refreshToken);
            return true;
        } catch {
            this.expireSession();
            return false;
        }
    }

    private expireSession(): void {
        useAuthStore.getState().expireSession();
    }

    private mapError(status: number, body: unknown): AbstractError {
        const message = this.extractMessage(body);
        const args = this.extractArgs(body);
        switch (status) {
            case 400:
                return new BadRequestError(message, args);
            case 401:
                return new UnauthorizedError(message, args);
            case 403:
                return new ForbiddenError(message, args);
            case 404:
                return new NotFoundError(message, args);
            default:
                return new ServerError(message, args);
        }
    }

    private extractArgs(body: unknown): string[] | undefined {
        if (body && typeof body === 'object') {
            const b = body as Record<string, unknown>;
            if (Array.isArray(b.details)) {
                const first = b.details[0];
                if (first && typeof first === 'object') {
                    const args = (first as Record<string, unknown>).args;
                    if (Array.isArray(args) && args.every((a) => typeof a === 'string')) {
                        return args;
                    }
                }
            }
        }
        return undefined;
    }

    private extractMessage(body: unknown): string | undefined {
        if (body && typeof body === 'object') {
            const b = body as Record<string, unknown>;
            const err = b.error;
            if (err && typeof err === 'object') {
                const msg = (err as Record<string, unknown>).message;
                if (typeof msg === 'string') {
                    return msg;
                }
            }
            if (typeof b.message === 'string') {
                return b.message;
            }
            if (Array.isArray(b.details)) {
                const messages = b.details
                    .map((d: unknown) => (d && typeof d === 'object' ? (d as Record<string, unknown>).message : null))
                    .filter((m: unknown): m is string => typeof m === 'string');
                if (messages.length > 0) {
                    return messages.join('; ');
                }
            }
        }
        return undefined;
    }
}
